import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, InputBase, MenuItem, MenuList, Paper, Stack, Typography } from '@mui/material';
import Iconify from '../../components/iconify';
import { liveViewKey, toFrameFraction } from '../../utils/scraperScenarios';
import i18n from '../../i18n';

// Typed characters go out together once typing pauses this long, rather than one
// request per key.
const TEXT_BATCH_MS = 300;
// Wheel deltas are summed and sent at most this often.
const WHEEL_BATCH_MS = 150;

const withScheme = (text) => (/^[a-z][a-z0-9+.-]*:\/\//i.test(text) ? text : `https://${text}`);

/**
 * The remote browser, as JPEG frames streamed from x-scraper, with the page's
 * URL above it.
 *
 * Frames arrive many times a second and are a hundred-odd kilobytes of base64
 * each, so they never go through React: pushFrame() only stores the latest one
 * in a ref, and a requestAnimationFrame loop copies it onto the <img> when it has
 * changed. Frames that arrive faster than the screen repaints are skipped rather
 * than queued, and the component renders on the first frame, on reset() and when
 * the viewport size changes — never once per frame.
 *
 * `interactive` (a Debug or Record run, paused) makes it a remote control: a click
 * on the frame, the wheel, keys while it has focus, a paste and a URL typed above it
 * go to `onInput` as x-scraper's input — `{ type: 'click', x, y }` in fractions of
 * the frame, `wheel`, `text`, `key`, `goto`. A native select's options, which no
 * frame shows, come as `selectPrompt` and are answered through `onSelect`.
 *
 * Imperative handle: `pushFrame({ data, width, height })`, `reset()`, and
 * `flushInput()`, which sends what was typed but not yet sent — the panel calls it
 * before a command, so keys typed just before Continue reach the page first.
 */
const LiveBrowserView = forwardRef(function LiveBrowserView(
    { url, live, interactive, recording, onInput, selectPrompt, onSelect, onDismissSelect },
    ref
) {
    const imgRef = useRef(null);
    const frameBoxRef = useRef(null);
    const latestRef = useRef(null);
    const shownRef = useRef(null);
    const hasFrameRef = useRef(false);
    const [hasFrame, setHasFrame] = useState(false);
    const [frameSize, setFrameSize] = useState(null);
    const [ripple, setRipple] = useState(null);
    const [urlDraft, setUrlDraft] = useState(null);

    const onInputRef = useRef(onInput);
    onInputRef.current = onInput;
    const interactiveRef = useRef(interactive);
    interactiveRef.current = interactive;
    const textRef = useRef({ text: '', timer: 0 });
    const wheelRef = useRef({ deltaX: 0, deltaY: 0, at: null, timer: 0 });

    // Typed while interactive, so sent even if that has just ended.
    const flushText = useCallback(() => {
        const pending = textRef.current;
        clearTimeout(pending.timer);
        pending.timer = 0;
        if (pending.text) onInputRef.current?.({ type: 'text', text: pending.text });
        pending.text = '';
    }, []);

    useImperativeHandle(
        ref,
        () => ({
            pushFrame(frame) {
                if (!frame || typeof frame.data !== 'string') return;
                latestRef.current = frame;
                if (!hasFrameRef.current) {
                    hasFrameRef.current = true;
                    setHasFrame(true);
                }
            },
            flushInput() {
                flushText();
            },
            reset() {
                latestRef.current = null;
                shownRef.current = null;
                hasFrameRef.current = false;
                setHasFrame(false);
                setFrameSize(null);
                imgRef.current?.removeAttribute('src');
            }
        }),
        [flushText]
    );

    useEffect(() => {
        let raf = 0;
        let size = null;
        const paint = () => {
            const frame = latestRef.current;
            if (frame && frame !== shownRef.current && imgRef.current) {
                shownRef.current = frame;
                imgRef.current.src = `data:image/jpeg;base64,${frame.data}`;
                // The viewport is set by the scenario and can change mid-run; state
                // only when it does, which is rarely.
                const next = frame.width && frame.height ? `${frame.width}×${frame.height}` : null;
                if (next !== size) {
                    size = next;
                    setFrameSize(next);
                }
            }
            raf = requestAnimationFrame(paint);
        };
        raf = requestAnimationFrame(paint);
        return () => cancelAnimationFrame(raf);
    }, []);

    const send = useCallback((input) => {
        if (interactiveRef.current) onInputRef.current?.(input);
    }, []);

    // Where on the frame a pointer is, or null outside it (the letterbox).
    const pointOf = useCallback((event) => {
        const img = imgRef.current;
        if (!img) return null;
        const width = img.naturalWidth || latestRef.current?.width;
        const height = img.naturalHeight || latestRef.current?.height;
        return toFrameFraction(event, img.getBoundingClientRect(), width, height);
    }, []);

    const onClick = (event) => {
        if (!interactive || event.button !== 0) return;
        const point = pointOf(event);
        if (!point) return;
        event.preventDefault();
        frameBoxRef.current?.focus({ preventScroll: true });
        flushText();
        send({ type: 'click', ...point });
        setRipple({ ...point, key: Date.now() });
    };

    const onKeyDown = (event) => {
        if (!interactive) return;
        const pressed = liveViewKey(event);
        if (!pressed) return;
        event.preventDefault();
        if (pressed.text) {
            const pending = textRef.current;
            pending.text += pressed.text;
            clearTimeout(pending.timer);
            pending.timer = setTimeout(flushText, TEXT_BATCH_MS);
        } else {
            flushText();
            send({ type: 'key', key: pressed.key });
        }
    };

    const onPaste = (event) => {
        if (!interactive) return;
        const text = event.clipboardData?.getData('text');
        if (!text) return;
        event.preventDefault();
        flushText();
        send({ type: 'text', text });
    };

    // Not React's onWheel, which is passive: the cabinet page must not scroll
    // while the remote one does.
    useEffect(() => {
        const box = frameBoxRef.current;
        if (!box || !interactive) return undefined;
        const wheel = wheelRef.current;
        const onWheel = (event) => {
            const point = pointOf(event);
            if (!point) return;
            event.preventDefault();
            wheel.deltaX += event.deltaX;
            wheel.deltaY += event.deltaY;
            wheel.at = point;
            if (wheel.timer) return;
            wheel.timer = setTimeout(() => {
                const { deltaX, deltaY, at } = wheel;
                wheel.deltaX = 0;
                wheel.deltaY = 0;
                wheel.timer = 0;
                send({ type: 'wheel', ...at, deltaX: Math.round(deltaX), deltaY: Math.round(deltaY) });
            }, WHEEL_BATCH_MS);
        };
        box.addEventListener('wheel', onWheel, { passive: false });
        return () => {
            box.removeEventListener('wheel', onWheel);
            clearTimeout(wheel.timer);
            wheel.timer = 0;
        };
    }, [interactive, pointOf, send]);

    // Leaving interactive mode sends what was typed, and forgets an edited URL.
    useEffect(() => {
        if (!interactive) {
            flushText();
            setUrlDraft(null);
        }
    }, [interactive, flushText]);

    useEffect(() => () => clearTimeout(textRef.current.timer), []);

    useEffect(() => {
        if (!ripple) return undefined;
        const timer = setTimeout(() => setRipple(null), 500);
        return () => clearTimeout(timer);
    }, [ripple]);

    const submitUrl = () => {
        const text = (urlDraft ?? '').trim();
        if (!text) return;
        flushText();
        send({ type: 'goto', url: withScheme(text) });
        setUrlDraft(null);
    };

    return (
        <Box
            sx={{
                border: 1,
                borderColor: interactive ? 'warning.main' : 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ px: 1.5, py: 0.75, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.neutral' }}
            >
                <Iconify icon="eva:globe-2-outline" width={16} sx={{ color: 'text.secondary', flexShrink: 0 }} />
                {interactive ? (
                    <InputBase
                        value={urlDraft ?? url ?? ''}
                        placeholder={i18n.t('scraperScenarios.urlPlaceholder')}
                        onChange={(event) => setUrlDraft(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') submitUrl();
                            if (event.key === 'Escape') setUrlDraft(null);
                        }}
                        inputProps={{ 'aria-label': i18n.t('scraperScenarios.urlPlaceholder'), spellCheck: false }}
                        sx={{ flex: 1, minWidth: 0, fontFamily: 'monospace', fontSize: 12 }}
                    />
                ) : (
                    <Typography
                        variant="caption"
                        noWrap
                        title={url || ''}
                        sx={{ fontFamily: 'monospace', flex: 1, minWidth: 0, color: url ? 'text.primary' : (theme) => theme.palette.text.placeholder }}
                    >
                        {url || i18n.t('scraperScenarios.noUrl')}
                    </Typography>
                )}
                {frameSize && (
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {frameSize}
                    </Typography>
                )}
                {recording && (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'error.main', flexShrink: 0 }}>
                        <Iconify icon="eva:radio-button-on-fill" width={14} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {i18n.t('scraperScenarios.rec')}
                        </Typography>
                    </Stack>
                )}
                {live && !recording && (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'error.main', flexShrink: 0 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main' }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {i18n.t('scraperScenarios.live')}
                        </Typography>
                    </Stack>
                )}
            </Stack>

            <Box
                ref={frameBoxRef}
                tabIndex={interactive ? 0 : -1}
                onKeyDown={onKeyDown}
                onPaste={onPaste}
                onBlur={flushText}
                aria-label={interactive ? i18n.t('scraperScenarios.liveViewInteractive') : undefined}
                sx={{
                    position: 'relative',
                    width: '100%',
                    // x-scraper's default viewport. Another one is letterboxed.
                    aspectRatio: '1280 / 800',
                    bgcolor: 'grey.900',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    outline: 'none',
                    '&:focus-visible': { boxShadow: (theme) => `inset 0 0 0 2px ${theme.palette.warning.main}` }
                }}
            >
                <Box
                    component="img"
                    ref={imgRef}
                    draggable={false}
                    onClick={onClick}
                    alt={i18n.t('scraperScenarios.liveViewAlt')}
                    sx={{
                        display: hasFrame ? 'block' : 'none',
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        cursor: interactive ? 'crosshair' : 'default',
                        userSelect: 'none'
                    }}
                />
                {!hasFrame && (
                    <Stack alignItems="center" spacing={1} sx={{ color: 'grey.500', px: 2, textAlign: 'center' }}>
                        <Iconify icon="eva:monitor-outline" width={36} />
                        <Typography variant="body2">{i18n.t('scraperScenarios.liveViewIdle')}</Typography>
                    </Stack>
                )}
                {ripple && (
                    <RippleAt ripple={ripple} img={imgRef.current} frame={latestRef.current} />
                )}
                {interactive && hasFrame && !selectPrompt && (
                    <Typography
                        variant="caption"
                        sx={{
                            position: 'absolute',
                            left: 8,
                            bottom: 8,
                            px: 1,
                            py: 0.25,
                            borderRadius: 0.5,
                            color: 'common.white',
                            bgcolor: 'rgba(0, 0, 0, 0.6)',
                            pointerEvents: 'none'
                        }}
                    >
                        {i18n.t(recording ? 'scraperScenarios.liveViewRecording' : 'scraperScenarios.liveViewInteractive')}
                    </Typography>
                )}
                {selectPrompt && (
                    <Paper
                        elevation={8}
                        sx={{ position: 'absolute', top: 8, right: 8, maxWidth: '60%', maxHeight: '80%', overflow: 'auto' }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, pt: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                                {i18n.t('scraperScenarios.selectPrompt')}
                            </Typography>
                            <Iconify
                                icon="eva:close-fill"
                                width={16}
                                role="button"
                                aria-label={i18n.t('scraperScenarios.selectDismiss')}
                                onClick={onDismissSelect}
                                sx={{ cursor: 'pointer', color: 'text.secondary' }}
                            />
                        </Stack>
                        <MenuList dense autoFocusItem>
                            {selectPrompt.options.map((option) => (
                                <MenuItem key={option.value} onClick={() => onSelect?.(option.value)}>
                                    {option.label || option.value}
                                </MenuItem>
                            ))}
                        </MenuList>
                    </Paper>
                )}
            </Box>
        </Box>
    );
});

/** A short-lived ring where a click landed, placed on the frame as drawn (letterbox included). */
function RippleAt({ ripple, img, frame }) {
    if (!img) return null;
    const box = img.getBoundingClientRect();
    const width = img.naturalWidth || frame?.width;
    const height = img.naturalHeight || frame?.height;
    if (!width || !height || !box.width || !box.height) return null;
    const scale = Math.min(box.width / width, box.height / height);
    const left = (box.width - width * scale) / 2 + ripple.x * width * scale;
    const top = (box.height - height * scale) / 2 + ripple.y * height * scale;
    return (
        <Box
            key={ripple.key}
            sx={{
                position: 'absolute',
                left: left - 10,
                top: top - 10,
                width: 20,
                height: 20,
                borderRadius: '50%',
                border: 2,
                borderColor: 'warning.main',
                pointerEvents: 'none',
                '@keyframes scraperRipple': { from: { opacity: 1, transform: 'scale(0.5)' }, to: { opacity: 0, transform: 'scale(1.6)' } },
                animation: 'scraperRipple 0.5s ease-out forwards'
            }}
        />
    );
}

RippleAt.propTypes = {
    ripple: PropTypes.object.isRequired,
    img: PropTypes.object,
    frame: PropTypes.object
};

LiveBrowserView.propTypes = {
    url: PropTypes.string,
    live: PropTypes.bool,
    interactive: PropTypes.bool,
    recording: PropTypes.bool,
    onInput: PropTypes.func,
    selectPrompt: PropTypes.shape({
        selector: PropTypes.string,
        options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string }))
    }),
    onSelect: PropTypes.func,
    onDismissSelect: PropTypes.func
};

export default LiveBrowserView;
